import React, { useMemo } from "react";
import { Box, Flex, Group, Skeleton, Text, Tooltip } from "@mantine/core";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import { OverflownText } from "../../../OverflownText";

const BADGE_COLOR = "#585C68";
const BADGE_BG = "#EFEFFD";

function labelFromItem(item: unknown): string {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item).trim();
  if (typeof item === "boolean") return String(item);
  if (typeof item === "object") {
    const o = item as Record<string, unknown>;
    const pick =
      (typeof o.name === "string" && o.name.trim()) ||
      (typeof o.label === "string" && o.label.trim()) ||
      (typeof o.title === "string" && o.title.trim()) ||
      (typeof o.displayName === "string" && o.displayName.trim());
    return pick || "";
  }
  return "";
}

export function normalizeBadgeLabels(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map(labelFromItem).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  const single = labelFromItem(value);
  return single ? [single] : [];
}

/**
 * Optional `renderParams.valueLabels` maps a value (keyed by `String(value)`) to the label
 * shown, e.g. `{ "true": "AI-created" }`. With `renderParams.hideUnmapped`, values missing
 * from the map are dropped, so `false` renders blank.
 */
export function applyValueLabels(
  labels: string[],
  valueLabels: unknown,
  hideUnmapped?: boolean,
): string[] {
  if (valueLabels == null || typeof valueLabels !== "object" || Array.isArray(valueLabels)) {
    return labels;
  }
  const map = valueLabels as Record<string, unknown>;
  return labels.flatMap((label) => {
    const mapped = map[label];
    if (typeof mapped === "string" && mapped.trim()) return [mapped.trim()];
    return hideUnmapped ? [] : [label];
  });
}

const BadgeChip: React.FC<{ label: string; maxWidth?: number }> = ({ label, maxWidth }) => (
  <OverflownText
    size="sm"
    style={{
      backgroundColor: BADGE_BG,
      color: BADGE_COLOR,
      padding: "0px 8px",
      borderRadius: 4,
      fontSize: 12,
      lineHeight: "20px",
      maxWidth: maxWidth ?? 100,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {label}
  </OverflownText>
);

export const BadgesCell: React.FC<any> = ({ data, col, renderParams }) => {
  const resolved = getNestedFieldValue(data, col.accessor);
  const labels = useMemo(() => {
    const fromField = applyValueLabels(
      normalizeBadgeLabels(resolved),
      renderParams?.valueLabels,
      renderParams?.hideUnmapped === true,
    );
    if (fromField.length > 0) return fromField;
    const fallback = renderParams?.default;
    if (typeof fallback === "string" && fallback !== "-") return [fallback];
    return [];
  }, [resolved, renderParams?.default, renderParams?.valueLabels, renderParams?.hideUnmapped]);

  const columnWidth =
    typeof col?.width === "number"
      ? col.width
      : typeof col?.width === "string"
        ? parseInt(col.width, 10)
        : undefined;

  if (labels.length === 0) {
    const emptyDisplay =
      typeof renderParams?.default === "string" ? renderParams.default : "";
    return (
      <Box p={0} m={0} h="100%" w="100%" sx={{ cursor: "default" }}>
        <Text size="sm" color="dimmed">
          {emptyDisplay}
        </Text>
      </Box>
    );
  }

  const maxChipWidth =
    columnWidth && labels.length === 1 ? Math.max(columnWidth - 16, 80) : 100;

  const visibleLimit = 3;
  const visible = labels.slice(0, visibleLimit);
  const overflow = labels.length - visible.length;

  return (
    <Box
      p={0}
      m={0}
      h="100%"
      w="100%"
      onMouseMove={(e) => e.stopPropagation()}
      sx={{ cursor: "default" }}
    >
      <Group spacing={6} noWrap sx={{ flexWrap: "nowrap", overflow: "hidden" }}>
        {visible.map((label) => (
          <BadgeChip key={label} label={label} maxWidth={maxChipWidth} />
        ))}
        {overflow > 0 && (
          <Tooltip label={labels.slice(visibleLimit).join(", ")} multiline withinPortal>
            <Text size="xs" color="dimmed" sx={{ flexShrink: 0 }}>
              +{overflow}
            </Text>
          </Tooltip>
        )}
      </Group>
    </Box>
  );
};

export const BadgesCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  const maxWidth = col.maxWidth;
  const chipWidths = columnWidth
    ? [Math.min(parseInt(String(columnWidth), 10) * 0.3, 72), Math.min(parseInt(String(columnWidth), 10) * 0.35, 88)]
    : [72, 88];

  return (
    <Box
      sx={{
        width: columnWidth || "100%",
        minWidth,
        maxWidth,
      }}
    >
      <Flex gap={6} wrap="nowrap">
        {chipWidths.map((w, i) => (
          <Skeleton key={i} height={22} width={w} radius="sm" />
        ))}
      </Flex>
    </Box>
  );
};
